/** `sections/tab-gallery.liquid` — `<tab-gallery>`: tabbed image gallery with switchable panels. */

const TAB_SELECTOR = '[role="tab"]'
const PANEL_SELECTOR = '[role="tabpanel"]'
const ACTIVE_TAB_CLASS = 'tab-gallery-tab-active'
const ACTIVE_PANEL_CLASS = 'tab-gallery-panel-active'

class TabGallery extends HTMLElement {
  #abort

  connectedCallback() {
    this.#abort = new AbortController()
    const { signal } = this.#abort

    this.addEventListener('click', this.#onClick, { signal })
    this.addEventListener('keydown', this.#onKeydown, { signal })
  }

  disconnectedCallback() {
    this.#abort?.abort()
  }

  get tabs() {
    return [...this.querySelectorAll(TAB_SELECTOR)]
  }

  get panels() {
    return [...this.querySelectorAll(PANEL_SELECTOR)]
  }

  #onClick = (event) => {
    const tab = event.target.closest(TAB_SELECTOR)
    if (!tab || tab.classList.contains(ACTIVE_TAB_CLASS)) return
    this.#activateTab(tab)
  }

  #onKeydown = (event) => {
    const tab = event.target.closest(TAB_SELECTOR)
    if (!tab) return

    const tabs = this.tabs
    const index = tabs.indexOf(tab)
    let next = -1

    switch (event.key) {
      case 'ArrowLeft':
        next = (index - 1 + tabs.length) % tabs.length
        break
      case 'ArrowRight':
        next = (index + 1) % tabs.length
        break
      case 'Home':
        next = 0
        break
      case 'End':
        next = tabs.length - 1
        break
      case 'Enter':
      case ' ':
        event.preventDefault()
        this.#activateTab(tab)
        return
      default:
        return
    }

    event.preventDefault()
    tabs[next].focus()
    this.#activateTab(tabs[next])
  }

  #activateTab(tab) {
    const panelId = tab.getAttribute('aria-controls')

    this.tabs.forEach((t) => {
      const isActive = t === tab
      t.classList.toggle(ACTIVE_TAB_CLASS, isActive)
      t.setAttribute('aria-selected', isActive ? 'true' : 'false')
      t.setAttribute('tabindex', isActive ? '0' : '-1')
    })

    this.panels.forEach((p) => {
      const isActive = p.id === panelId
      p.classList.toggle(ACTIVE_PANEL_CLASS, isActive)
      p.hidden = !isActive
    })
  }
}

if (!customElements.get('tab-gallery')) {
  customElements.define('tab-gallery', TabGallery)
}
